import { useSyncExternalStore } from 'react';

/**
 * Ayudas de la pantalla, guardadas en esta computadora y por usuario:
 * - setup: panel flotante de Puesta en marcha (abierto, minimizado u oculto).
 * - tips: botones "?" de recorrido en cada pantalla.
 * - toursSeen: recorridos ya vistos (el general se ofrece solo la primera vez).
 */
export interface HelpPrefs {
  setup: 'open' | 'min' | 'hidden';
  tips: boolean;
  toursSeen: string[];
}

const DEFAULT: HelpPrefs = { setup: 'open', tips: true, toursSeen: [] };
const KEY = 'bifeai.help.v1';

type Store = Record<string, HelpPrefs>;

function readAll(): Store {
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Store) : {};
  } catch {
    return {};
  }
}

let all: Store = typeof window === 'undefined' ? {} : readAll();
const listeners = new Set<() => void>();
// Mismo objeto mientras no cambie (lo necesita useSyncExternalStore).
const cache = new Map<string, { src: HelpPrefs | undefined; value: HelpPrefs }>();

export function getHelp(userId: string): HelpPrefs {
  const stored = all[userId];
  const hit = cache.get(userId);
  if (hit && hit.src === stored) return hit.value;
  const value = { ...DEFAULT, ...(stored ?? {}) };
  cache.set(userId, { src: stored, value });
  return value;
}

export function setHelp(userId: string, patch: Partial<HelpPrefs>): void {
  const next = { ...getHelp(userId), ...patch };
  all = { ...all, [userId]: next };
  cache.set(userId, { src: next, value: next });
  try {
    window.localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    /* sin almacenamiento: queda solo mientras esté abierta la página */
  }
  listeners.forEach((l) => l());
}

export function useHelp(userId: string): HelpPrefs {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => getHelp(userId),
    () => getHelp(userId),
  );
}

/** Pide abrir un recorrido guiado desde cualquier parte (el menú de usuario, un botón "?"). */
export function startTour(id: string): void {
  window.dispatchEvent(new CustomEvent('bifeai:tour', { detail: id }));
}
